import React, { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import './ChartCard.css';

const ChartCard = React.memo(({ title, data, categories, colors, values = [] }) => {
  const [selectedRange, setSelectedRange] = useState('6 Months');

  const rangeOptions = useMemo(() => ({
    '1 Month': 1,
    '3 Months': 3,
    '6 Months': 6,
  }), []);

const filteredData = useMemo(() => {
  return data.slice(-rangeOptions[selectedRange]);
}, [data, selectedRange, rangeOptions]);

const getDataPropertyName = useMemo(() => category => {
    const mappings = {
      'Deliveries': 'deliveries',
      'Earnings': 'earnings', 
      'Pending': 'pending',
      'Refunds': 'refunds',
      'Delivered': 'delivered',
      'Fresh': 'fresh',
      'On Hold': 'onhold',
      'Revisions': 'revisions'
    };
    return mappings[category] || category.toLowerCase().replace(/ /g, '');
  }, []);

    const chartDatasets = useMemo(() => (
    categories.map((cat, idx) => ({
    label: cat,
    backgroundColor: colors[idx],
    borderRadius: 2,
    data: filteredData.map(row => row[getDataPropertyName(cat)] ?? 0),
    barPercentage:0.8,
    categoryPercentage:0.4,
   }))
), [categories, colors, filteredData, getDataPropertyName]);

  const chartData = useMemo(() => ({
    labels: filteredData.map(row => row.month),
    datasets: chartDatasets
  }), [filteredData, chartDatasets]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 10000,
          callback: value => `${value / 1000}k`
        },
        grid: {
          drawTicks: false,
          drawBorder: true,
        }
      }
    }
  };

  const renderRangeSelectors = () => {
    const abbreviatedLabels = { '1 Month': '1M', '3 Months': '3M', '6 Months': '6M' };
    return (
      <div className="range-selectors">
        {Object.keys(rangeOptions).map(range => (
          <div
            key={range}
            onClick={() => setSelectedRange(range)}
            className={`range-selector ${selectedRange === range ? 'active' : ''}`}
            onMouseEnter={e => {
              if (selectedRange !== range) e.currentTarget.style.borderColor = '#6b7280';
            }}
            onMouseLeave={e => {
              if (selectedRange !== range) e.currentTarget.style.borderColor = 'transparent';
            }}
          >
            {abbreviatedLabels[range]}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="custom-card p-3 mb-0" style={{ minWidth: 320 }}>
      <div className="chart-header">
        <h5 className="chart-title">
          {title}
        </h5>
        {renderRangeSelectors()}
      </div>

      <div className="chart-content">
        <div className="chart-categories">
          {categories.map((category, idx) => (
            <div
              key={category}
              className="category-item"
            >
              <div className="category-value">{values[idx] !== undefined ? values[idx] : '-'}</div>
              <div className="category-label">{category}</div>
            </div>
          ))}
        </div>

        <div className="chart-container">
          <Bar data={chartData} options={chartOptions} />
        </div>

        <div className="chart-legend">
          {categories.map((category, idx) => (
            <div
              key={category}
              className="legend-item"
            >
              <span
                className="legend-dot"
                style={{ backgroundColor: colors[idx] }}
              ></span>
              <span className="legend-label">{category}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

export default ChartCard;
